import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Autocomplete,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/errorMessage';
import { useProfessionals } from '../../../hooks/useProfessionals';
import { useSaveAestheticProtocol } from './hooks/useSaveAestheticProtocol';

export default function AestheticProtocolModal({ open, onClose, onSave, patientId }) {
  const navigate = useNavigate();

  const { professionals: comboBoxDentists } = useProfessionals();
  const { save: saveProtocolRequest } = useSaveAestheticProtocol(patientId);

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [description, setDescription] = useState('');
  const [selectedDentistId, setSelectedDentistId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const dentistIdFromUser = sessionStorage.getItem('dentistIdFromUser');
    if (dentistIdFromUser && dentistIdFromUser !== 'null' && dentistIdFromUser !== 'undefined') {
      setSelectedDentistId(dentistIdFromUser);
    }
  }, [open]);

  const handleSave = (event) => {
    event.preventDefault();

    if (!description.trim() || !selectedDentistId) {
      setError('Todos os campos são obrigatórios!');
      return;
    }

    setLoading(true);
    saveProtocolRequest({ description, professionalId: selectedDentistId })
      .then(() => {
        setLoading(false);
        setDescription('');
        setSelectedDentistId('');
        setError('');
        onSave();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const handleClose = () => {
    setDescription('');
    setSelectedDentistId('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Loading isToHidden={isLoading === false} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}
      <DialogTitle>Novo Protocolo Estético</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Grid container sx={{ mb: 2 }}>
            <Grid item size={12}>
              <span style={{ color: 'red', fontSize: '0.875rem' }}>{error}</span>
            </Grid>
          </Grid>
        )}
        <form id="new-protocol-form" onSubmit={handleSave}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item size={12}>
              <TextField
                fullWidth
                label="Descrição do Protocolo"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item size={12}>
              <Autocomplete
                options={comboBoxDentists}
                getOptionLabel={(option) => option.nome ?? ''}
                value={comboBoxDentists.find((d) => d.id === selectedDentistId) ?? null}
                onChange={(_, newValue) => setSelectedDentistId(newValue?.id ?? '')}
                noOptionsText="Nenhum profissional encontrado."
                renderInput={(params) => <TextField {...params} fullWidth label="Profissional" />}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button type="submit" form="new-protocol-form" variant="contained" disabled={isLoading}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AestheticProtocolModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  patientId: PropTypes.string,
};

AestheticProtocolModal.defaultProps = {
  patientId: null,
};
